import { useState } from 'react';
import {
  Button,
  Textarea,
  Text,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Send24Regular } from '@fluentui/react-icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useComments, useAddComment } from '@/api/hooks/useReferrals';

dayjs.extend(relativeTime);

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  thread: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxHeight: '300px',
    overflowY: 'auto',
  },
  comment: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-end',
  },
  emptyState: {
    padding: tokens.spacingVerticalS,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

interface CommentThreadProps {
  referralId: number;
  authorEmail: string;
  authorName: string;
}

export function CommentThread({ referralId, authorEmail, authorName }: CommentThreadProps) {
  const styles = useStyles();
  const [content, setContent] = useState('');
  const { data: comments, isLoading } = useComments(referralId);
  const addComment = useAddComment();

  const handleSubmit = async () => {
    if (!content.trim()) return;
    await addComment.mutateAsync({
      referralId,
      data: { content: content.trim(), authorEmail, authorName },
    });
    setContent('');
  };

  return (
    <div className={styles.root}>
      {isLoading ? (
        <Spinner size="tiny" label="Loading comments..." />
      ) : comments && comments.length > 0 ? (
        <div className={styles.thread}>
          {comments.map((c) => (
            <div key={c.id} className={styles.comment}>
              <div className={styles.commentHeader}>
                <Text size={200} weight="semibold">
                  {c.authorName}
                </Text>
                <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                  {dayjs(c.createdAt).fromNow()}
                </Text>
              </div>
              <Text size={300} style={{ whiteSpace: 'pre-wrap' }}>
                {c.content}
              </Text>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Text size={200}>No comments yet</Text>
        </div>
      )}

      <div className={styles.inputRow}>
        <Textarea
          size="small"
          placeholder="Add a comment..."
          value={content}
          onChange={(_e, d) => setContent(d.value)}
          rows={2}
          resize="vertical"
          style={{ flexGrow: 1 }}
        />
        <Button
          appearance="primary"
          size="small"
          icon={addComment.isPending ? <Spinner size="tiny" /> : <Send24Regular />}
          disabled={!content.trim() || addComment.isPending}
          onClick={() => void handleSubmit()}
          aria-label="Send comment"
        />
      </div>
    </div>
  );
}
